import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  useCreateServiceCategory,
  useUpdateServiceCategory,
} from "../hooks/categories.hooks";
import { ServiceCategory } from "@/types/serviceCategory";
import { useHotel } from "@/hooks/useHotel";
import { FormField } from "../../reservations/guests/components/form/FormFields";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
});

type FormValues = z.infer<typeof formSchema>;

interface ServiceCategoryFormProps {
  category?: ServiceCategory;
  onSuccess: () => void;
  onCancel: () => void;
}

export const ServiceCategoryForm: React.FC<ServiceCategoryFormProps> = ({
  category,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { hotelId } = useHotel();

  const { mutate: createCategory, isPending: isCreating } =
    useCreateServiceCategory();
  const { mutate: updateCategory, isPending: isUpdating } =
    useUpdateServiceCategory();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: category?.name || "",
    },
  });

  const onSubmit = (values: FormValues) => {
    if (category) {
      updateCategory(
        { id: category.id, data: { ...values, hotelId: hotelId! } },
        { onSuccess },
      );
    } else {
      createCategory({ ...values, hotelId: hotelId! }, { onSuccess });
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      <FormField
        id="name"
        label="NAME"
        required
        placeholder="Name"
        register={form.register}
        error={form.formState.errors.name?.message}
      />
      <div className="flex justify-end space-x-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          {t("common.cancel", "Cancel")}
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading && t("common.saving", "Saving...")}
          {!isLoading && category && t("common.save", "Save")}
          {!isLoading && !category && t("common.create", "Create")}
        </Button>
      </div>
    </form>
  );
};
